"use client";

import { useEffect, useRef, useState } from "react";
import { useAppForeground } from "./use-app-foreground";

/**
 * A clock the render can read, for labels that go stale on their own.
 *
 * Returns `null` until the component has mounted, so server-rendered HTML never
 * bakes in the server's clock or timezone — the same two-pass pattern as
 * `useLocalStartTimeLabel`. Callers render a placeholder for that first pass.
 *
 * `getIntervalMs` is asked, after every tick, how long to wait for the next
 * one: return `1000` while seconds are on screen, `60_000` while minutes are,
 * and `null` when nothing on screen can change again — no timer is scheduled at
 * all in that case, so a page full of settled dates costs nothing.
 *
 * Second- and minute-length waits are aligned to the wall clock, so a minute
 * label flips when the minute does rather than an arbitrary fraction of a second later.
 *
 * Ticks stop while the app is in the background — the OS suspends timers there
 * anyway, and a WebView that wakes up mid-interval would otherwise show a stale
 * number until the next one. `useAppForeground` covers both a native resume and
 * a web tab becoming visible, and re-reads the clock immediately on return.
 */
export function useNow(
  getIntervalMs: (now: Date) => number | null,
): Date | null {
  const [now, setNow] = useState<Date | null>(null);

  // A plain ref, not `useEffectEvent`: its identity is not stable, and this is
  // only ever read from a timer callback that runs long after the render.
  const getIntervalRef = useRef(getIntervalMs);
  getIntervalRef.current = getIntervalMs;

  const restartRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      const current = new Date();
      setNow(current);

      const interval = getIntervalRef.current(current);
      if (interval == null) return;

      // Backgrounded: the foreground listener restarts us.
      if (typeof document !== "undefined" && document.hidden) return;

      const aligned =
        interval === 1000 || interval === 60_000
          ? interval - (current.getTime() % interval)
          : interval;

      timer = setTimeout(tick, Math.max(200, aligned));
    };

    const restart = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      tick();
    };

    restartRef.current = restart;
    tick();

    return () => {
      cancelled = true;
      restartRef.current = null;
      if (timer) clearTimeout(timer);
    };
  }, []);

  useAppForeground(() => {
    restartRef.current?.();
  });

  return now;
}
